import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, Users, Calendar, Mail, FileText, CreditCard, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import heroImage from "@/assets/hero-campsite-field.jpg";
import customerDashboardPreview from "@/assets/customer-dashboard-preview.png";
import paymentManagementPreview from "@/assets/payment-management-preview.png";
import bookingCalendarPreview from "@/assets/booking-calendar-preview.png";
import guestCommunicationsPreview from "@/assets/guest-communications-preview.png";
import reportsAnalyticsPreview from "@/assets/reports-analytics-preview.png";
import facilityManagementPreview from "@/assets/facility-management-preview.png";
import { useSubdomain } from "@/components/SubdomainRouter";
import TenantLanding from "./TenantLanding";
import { HomepagePopup } from "@/components/HomepagePopup";
import { SEO } from "@/components/SEO";

const Landing = () => {
  const { isSubdomain, tenantSlug } = useSubdomain();

  // If on a subdomain, show the tenant landing page instead
  if (isSubdomain && tenantSlug) {
    return <TenantLanding subdomainSlug={tenantSlug} />;
  }

  const screenshots = [
    { src: customerDashboardPreview, alt: "Customer Dashboard", title: "Customer Dashboard" },
    { src: bookingCalendarPreview, alt: "Booking Calendar View", title: "Booking Calendar" },
    { src: paymentManagementPreview, alt: "Payment Management", title: "Payment Management" },
    { src: facilityManagementPreview, alt: "Facility Management", title: "Facility Management" },
    { src: reportsAnalyticsPreview, alt: "Reports Dashboard", title: "Reports & Analytics" },
    { src: guestCommunicationsPreview, alt: "Email Communications", title: "Guest Communications" }
  ];

  const features = [
    {
      icon: Shield,
      title: "Secure Role-Based Access",
      description: "Three levels of access control: Admin, Manager, and User with granular permissions.",
    },
    {
      icon: Calendar,
      title: "Smart Booking Management",
      description: "Intuitive calendar views, availability tracking, and automated confirmations.",
    },
    {
      icon: Users,
      title: "Guest Self-Service",
      description: "Let guests register, book, and manage their reservations independently.",
    },
    {
      icon: Mail,
      title: "Automated Communications",
      description: "Templated emails, automated reminders, and milestone notifications.",
    },
    {
      icon: FileText,
      title: "Digital Documentation",
      description: "Pre-populated check-in forms and customisable check-out checklists.",
    },
    {
      icon: CreditCard,
      title: "Integrated Payments",
      description: "Secure online payments, deposit tracking, and balance management.",
    },
  ];

  const benefits = [
    "Configure facilities (pitches, cabins, pods, halls) with custom pricing",
    "Manage activities (archery, kayaking, team-building) and schedules",
    "Set up global settings, taxes, discounts, and payment rules",
    "Generate and export booking documents with guest details",
    "Track payment status and send automated balance reminders",
    "Maintain complete audit trail of all bookings and changes",
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Campsite Booking & Management Platform"
        description="Professional campsite booking system with secure payments, automated communications, facility management, and comprehensive reporting. Streamline your outdoor venue operations."
        url="/"
      />
      <Navbar />
      <HomepagePopup />

      {/* Hero Section */}
      <section className="relative pt-40 md:pt-52 pb-32 md:pb-40 min-h-[85vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage}
            alt="Beautiful green campsite field with trees"
            className="w-full h-full object-cover"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 via-40% to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent via-50% to-muted" />
        </div>

        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold mb-6 text-white drop-shadow-lg">
              Campsite Management,
              <br />
              Simplified
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl mx-auto drop-shadow-md">
              Secure, role-based booking platform that streamlines operations
              from browsing to check-out
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/join">
                <Button size="lg" className="bg-gradient-hero hover:opacity-90 transition-opacity text-lg px-8">
                  Join SiteBook
                </Button>
              </Link>
              <Link to="/auth">
                <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white text-white hover:bg-white hover:text-primary">
                  Sign In
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              Everything You Need
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Complete end-to-end workflow for modern campsite management
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <Card
                key={feature.title}
                className="shadow-card hover:shadow-elevated transition-all duration-300 animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardHeader>
                  <div className="p-3 bg-gradient-hero rounded-lg w-fit mb-4">
                    <feature.icon className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                  <CardDescription className="text-base">
                    {feature.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
                Powerful Admin Controls
              </h2>
              <p className="text-xl text-muted-foreground">
                Configure every aspect of your campsite operations
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-4 rounded-lg bg-card shadow-soft hover:shadow-card transition-shadow animate-slide-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CheckCircle className="h-6 w-6 text-accent shrink-0 mt-1" />
                  <p className="text-foreground">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Screenshots Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              See SiteBook in Action
            </h2>
            <p className="text-xl text-muted-foreground">
              Powerful features designed for campsite operations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {screenshots.map((screenshot, index) => (
              <Card 
                key={index} 
                className="overflow-hidden hover:shadow-lg transition-shadow animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <img 
                  src={screenshot.src} 
                  alt={screenshot.alt}
                  className="w-full h-48 object-cover object-top"
                  loading="lazy"
                />
                <CardContent className="p-4">
                  <h3 className="font-semibold">{screenshot.title}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-hero">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 text-primary-foreground">
              Ready to Transform Your Campsite?
            </h2>
            <p className="text-xl text-primary-foreground/90 mb-8">
              Join modern campsite operators who've streamlined their operations
              with SiteBook
            </p>
            <Link to="/join">
              <Button
                size="lg"
                variant="secondary"
                className="text-lg px-8 hover:scale-105 transition-transform"
              >
                Join SiteBook
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border bg-card">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-muted-foreground">
            <p>&copy; 2025 SiteBook. Secure campsite management platform.</p>
            <p className="text-sm text-muted-foreground/70 mt-2">
              Trakto Software Ltd - Registered in England & Wales 16886380 - 20-22 Wenlock Road, London, N1 7GU
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
